import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';

const DEFAULT_PROPERTIES = {
  width: '100',
  height: '',
  cellSpacing: '0',
  cellPadding: '8',
  borderWidth: '1',
  align: 'left',
  borderStyle: 'solid',
  borderColor: '#C8D3DE',
  backgroundColor: '',
};

const BORDER_STYLES = [
  { value: 'solid', label: 'Sólida' },
  { value: 'dashed', label: 'Tracejada' },
  { value: 'dotted', label: 'Pontilhada' },
  { value: 'double', label: 'Dupla' },
  { value: 'none', label: 'Nenhuma' },
];

const parseStyleMap = (style) => {
  if (!style) return {};
  return style.split(';').reduce((acc, part) => {
    const [rawKey, ...rest] = part.split(':');
    if (!rawKey || rest.length === 0) return acc;
    acc[rawKey.trim().toLowerCase()] = rest.join(':').trim();
    return acc;
  }, {});
};

const readNumber = (value, fallback = '') => {
  if (!value) return fallback;
  const match = value.match(/[\d.]+/);
  return match ? match[0] : fallback;
};

export const readTableProperties = (editor) => {
  const attrs = editor.getAttributes('table') || {};
  const styleMap = parseStyleMap(attrs.style);

  let cellStyleMap = {};
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    if (node.type.name === 'table') {
      node.descendants((child) => {
        if (
          !Object.keys(cellStyleMap).length &&
          (child.type.name === 'tableCell' || child.type.name === 'tableHeader')
        ) {
          cellStyleMap = parseStyleMap(child.attrs.style);
          return false;
        }
        return true;
      });
      break;
    }
  }

  let align = 'left';
  const marginLeft = styleMap['margin-left'] || '';
  const marginRight = styleMap['margin-right'] || '';
  if (marginLeft === 'auto' && marginRight === 'auto') align = 'center';
  else if (marginLeft === 'auto') align = 'right';

  const widthRaw = styleMap.width || '100%';
  const width = widthRaw.includes('%') ? readNumber(widthRaw, '100') : readNumber(widthRaw, '100');

  const borderSource = {
    ...styleMap,
    ...cellStyleMap,
  };

  if ((!borderSource['border-style'] || !borderSource['border-color']) && borderSource.border) {
    const tokens = borderSource.border.trim().split(/\s+/);
    if (!borderSource['border-width'] && tokens[0]) {
      borderSource['border-width'] = tokens[0];
    }
    if (!borderSource['border-style'] && tokens[1]) {
      borderSource['border-style'] = tokens[1];
    }
    if (!borderSource['border-color'] && tokens.length >= 3) {
      borderSource['border-color'] = tokens.slice(2).join(' ');
    }
  }

  return {
    width,
    height: readNumber(styleMap.height, ''),
    cellSpacing: attrs.cellSpacing || readNumber(styleMap['border-spacing'], '0'),
    cellPadding: attrs.cellPadding || readNumber(cellStyleMap.padding, '8'),
    borderWidth: readNumber(borderSource['border-width'] || borderSource.border, '1'),
    align,
    borderStyle: borderSource['border-style'] || 'solid',
    borderColor: borderSource['border-color'] || '#C8D3DE',
    backgroundColor:
      borderSource['background-color'] && borderSource['background-color'] !== 'transparent'
        ? borderSource['background-color']
        : '',
  };
};

const buildTableStyle = (properties) => {
  const parts = [
    'border-collapse:separate',
    `border-spacing:${properties.cellSpacing || 0}px`,
    'table-layout:fixed',
    'width:auto',
    'max-width:100%',
  ];

  if (properties.width && properties.width !== '100') {
    parts.push(`width:${properties.width}%`);
  }

  if (properties.height) {
    parts.push(`height:${properties.height}px`);
  }

  if (properties.borderStyle === 'none' || properties.borderWidth === '0') {
    parts.push('border:none');
  } else {
    parts.push(
      `border-width:${properties.borderWidth || 1}px`,
      `border-style:${properties.borderStyle || 'solid'}`,
      `border-color:${properties.borderColor || '#C8D3DE'}`
    );
  }

  if (properties.backgroundColor && properties.backgroundColor !== 'transparent') {
    parts.push(`background-color:${properties.backgroundColor}`);
  } else if (properties.backgroundColor === 'transparent') {
    parts.push('background-color:transparent');
  }

  if (properties.align === 'center') {
    parts.push('margin-left:auto', 'margin-right:auto');
  } else if (properties.align === 'right') {
    parts.push('margin-left:auto', 'margin-right:0');
  } else {
    parts.push('margin-left:0', 'margin-right:auto');
  }

  return parts.join(';');
};

const upsertStyleValue = (style, key, value) => {
  const map = parseStyleMap(style);
  if (!value) {
    delete map[key.toLowerCase()];
  } else {
    map[key.toLowerCase()] = value;
  }
  return Object.entries(map)
    .map(([k, v]) => `${k}: ${v}`)
    .join('; ');
};

const buildCellBorderStyle = (properties) => {
  if (properties.borderStyle === 'none' || properties.borderWidth === '0') {
    return [
      { key: 'border', value: 'none' },
      { key: 'border-width', value: '0' },
      { key: 'border-style', value: 'none' },
    ];
  }

  const width = `${properties.borderWidth || 1}px`;
  const style = properties.borderStyle || 'solid';
  const color = properties.borderColor || '#C8D3DE';

  return [
    { key: 'border', value: `${width} ${style} ${color}` },
    { key: 'border-width', value: width },
    { key: 'border-style', value: style },
    { key: 'border-color', value: color },
  ];
};

export const applyTableProperties = (editor, properties) => {
  const tableStyle = buildTableStyle(properties);
  const padding = `${properties.cellPadding || 0}px`;
  const cellBorders = buildCellBorderStyle(properties);

  editor
    .chain()
    .focus()
    .command(({ tr, state, dispatch }) => {
      const { $from } = state.selection;
      let tablePos = null;
      let tableNode = null;

      for (let depth = $from.depth; depth > 0; depth -= 1) {
        const node = $from.node(depth);
        if (node.type.name === 'table') {
          tablePos = $from.before(depth);
          tableNode = node;
          break;
        }
      }

      if (tablePos === null || !tableNode || !dispatch) {
        return false;
      }

      tr.setNodeMarkup(tablePos, undefined, {
        ...tableNode.attrs,
        style: tableStyle,
        cellPadding: properties.cellPadding || '0',
        cellSpacing: properties.cellSpacing || '0',
      });

      tableNode.descendants((node, pos) => {
        if (node.type.name !== 'tableCell' && node.type.name !== 'tableHeader') {
          return;
        }

        const absolutePos = tablePos + 1 + pos;
        let nextStyle = upsertStyleValue(node.attrs.style, 'padding', padding);

        cellBorders.forEach(({ key, value }) => {
          nextStyle = upsertStyleValue(nextStyle, key, value);
        });

        if (properties.backgroundColor) {
          nextStyle = upsertStyleValue(
            nextStyle,
            'background-color',
            properties.backgroundColor === 'transparent' ? 'transparent' : properties.backgroundColor
          );
        }

        tr.setNodeMarkup(absolutePos, undefined, {
          ...node.attrs,
          style: nextStyle || null,
        });
      });

      dispatch(tr);
      return true;
    })
    .run();
};

const TablePropertiesDialog = ({ open, editor, onClose }) => {
  const [tab, setTab] = useState(0);
  const [properties, setProperties] = useState(DEFAULT_PROPERTIES);

  useEffect(() => {
    if (open) {
      setTab(0);
      setProperties(readTableProperties(editor));
    }
  }, [open, editor]);

  const updateField = (key, value) => {
    setProperties((current) => ({ ...current, [key]: value }));
  };

  const handleSave = () => {
    applyTableProperties(editor, properties);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Propriedades da tabela</DialogTitle>
      <Tabs
        value={tab}
        onChange={(_event, value) => setTab(value)}
        sx={{ px: 2, borderBottom: '1px solid #E0E0E0' }}
      >
        <Tab label="Geral" />
        <Tab label="Avançado" />
      </Tabs>
      <DialogContent sx={{ pt: 2 }}>
        {tab === 0 ? (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Largura (%)"
              type="number"
              size="small"
              value={properties.width}
              onChange={(e) => updateField('width', e.target.value)}
              inputProps={{ min: 1, max: 100 }}
              fullWidth
            />
            <TextField
              label="Altura (px)"
              type="number"
              size="small"
              value={properties.height}
              onChange={(e) => updateField('height', e.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label="Espaço entre células (px)"
              type="number"
              size="small"
              value={properties.cellSpacing}
              onChange={(e) => updateField('cellSpacing', e.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label="Padding da célula (px)"
              type="number"
              size="small"
              value={properties.cellPadding}
              onChange={(e) => updateField('cellPadding', e.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <TextField
              label="Largura da borda (px)"
              type="number"
              size="small"
              value={properties.borderWidth}
              onChange={(e) => updateField('borderWidth', e.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Alinhamento</InputLabel>
              <Select
                label="Alinhamento"
                value={properties.align}
                onChange={(e) => updateField('align', e.target.value)}
              >
                <MenuItem value="left">Esquerda</MenuItem>
                <MenuItem value="center">Centro</MenuItem>
                <MenuItem value="right">Direita</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Estilo da borda</InputLabel>
              <Select
                label="Estilo da borda"
                value={properties.borderStyle}
                onChange={(e) => updateField('borderStyle', e.target.value)}
              >
                {BORDER_STYLES.map((style) => (
                  <MenuItem key={style.value} value={style.value}>
                    {style.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Cor da borda"
              type="color"
              size="small"
              value={properties.borderColor || '#C8D3DE'}
              onChange={(e) => updateField('borderColor', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Cor de fundo"
              type="color"
              size="small"
              value={properties.backgroundColor || '#FFFFFF'}
              onChange={(e) => updateField('backgroundColor', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Button size="small" onClick={() => updateField('backgroundColor', 'transparent')}>
              Remover fundo
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#0D3D5C' }}>
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TablePropertiesDialog;
